import { useEffect, useMemo, useRef, useState } from "react";
import { useTenantOrganization } from "../../../lib/api";
import {
  dealerRows,
  indexOrg,
  organizationTree,
  regionNamesForDealer,
} from "../../../lib/organization";
import {
  useDealerEditor,
  useRegionEditor,
  useWorkshopEditor,
} from "../../SuperAdmin/useOrgEditors";
import { Icon } from "../../../components/msd/Icon";
import {
  Btn,
  CARD,
  Dot,
  Empty,
  Field,
  LinkBtn,
  Page,
  PageHead,
  Sheet,
  cx,
} from "../../../components/msd/ui";
import WorkshopSheet from "./WorkshopSheet";
import {
  ConfirmDelete,
  FieldGroup,
  OrgError,
  OrgLoading,
  Picker,
  RemoveBtn,
  SMALL_INPUT,
  plural,
} from "./kit";

/** "+ Add" with a small menu of what this login may add. */
function AddMenu({ items }) {
  const [open, setOpen] = useState(false);
  const box = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => !box.current?.contains(e.target) && setOpen(false);
    const esc = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", esc);
    };
  }, [open]);
  if (!items.length) return null;
  return (
    <div ref={box} className="relative">
      <Btn
        variant="pri"
        icon="plus2"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        Add
      </Btn>
      {open && (
        <ul
          role="menu"
          className="absolute right-0 top-[calc(100%+6px)] z-30 m-0 min-w-[170px] list-none rounded-[12px] bg-msd-surface p-1 shadow-msd-pop"
        >
          {items.map(([label, run]) => (
            <li key={label} role="none">
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  run();
                }}
                className="flex w-full cursor-pointer items-center rounded-[8px] border-0 bg-transparent px-[10px] py-2 text-left text-[14px] text-msd-ink hover:bg-msd-soft focus-visible:bg-msd-soft focus-visible:outline-none"
              >
                {label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Organization: one table of every workshop, grouped under its region and dealer,
 * with its status, team size and whether every role is filled. Quick views pick out
 * the workshops that need staff or are paused; a row opens the workshop. What used to
 * be the Regions, Dealers and Workshops pages. Each part
 * keeps its own page's permission: a login sees only the parts it could open
 * before, and changes only the parts it could change.
 *
 * @param access  useOrgAccess() — { regions, dealers, workshops } × { see, edit }
 */
export default function OrganizationPage({ access, tenantId = null }) {
  const { data, isLoading, isError, error, refetch } =
    useTenantOrganization(tenantId);
  const [filters, setFilters] = useState({
    query: "",
    region: "all",
    dealer: "all",
    status: "all",
  });
  const [view, setView] = useState("all");
  // The workshop whose sheet is open.
  const [openShop, setOpenShop] = useState(null);

  const ix = useMemo(() => indexOrg(data), [data]);
  const see = useMemo(
    () => ({
      regions: access.regions.see,
      dealers: access.dealers.see,
      workshops: access.workshops.see,
    }),
    [access.regions.see, access.dealers.see, access.workshops.see],
  );
  const all = useMemo(() => organizationTree(ix, {}, see), [ix, see]);
  const tree = useMemo(
    () => organizationTree(ix, filters, see),
    [ix, filters, see],
  );
  const dealerRowById = useMemo(
    () => new Map(dealerRows(ix).map((d) => [d.id, d])),
    [ix],
  );

  const region = useRegionEditor(tenantId, ix);
  const dealer = useDealerEditor(tenantId, ix);
  const workshop = useWorkshopEditor(tenantId, ix);

  if (isLoading) return <OrgLoading />;
  if (isError) return <OrgError error={error} onRetry={refetch} />;

  const canRegion = access.regions.edit;
  const canDealer = access.dealers.edit;
  const canWorkshop = access.workshops.edit;
  const set = (patch) => setFilters((f) => ({ ...f, ...patch }));
  const counts = [
    see.regions && plural(ix.regions.length, "region", "regions"),
    (see.dealers || see.workshops) &&
      plural(ix.dealers.length, "dealer", "dealers"),
    see.workshops && plural(ix.workshops.length, "workshop", "workshops"),
  ].filter(Boolean);
  const nothing =
    !ix.regions.length && !ix.dealers.length && !ix.workshops.length;

  // Each sheet hides while its delete is being confirmed, so Escape closes
  // one thing at a time.
  const deleteAndClose = (editor, close) => async () => {
    if (await editor.confirmDelete()) close();
  };

  const needsStaff = (w) => w.unfilled > 0;
  const shown = (w) =>
    view === "all" || (view === "gaps" ? needsStaff(w) : w.status === "paused");
  const allShops = all.flatMap((r) => r.dealers.flatMap((d) => d.workshops));
  const gapCount = allShops.filter(needsStaff).length;
  const pausedCount = allShops.filter((w) => w.status === "paused").length;

  const CELL = "px-4 py-3 align-middle";
  const HEAD =
    "px-4 py-[10px] text-left text-[12px] font-medium uppercase tracking-[.04em] text-msd-muted";
  const cols = see.workshops ? 6 : 2;

  // The dealer's name, which opens the dealer to edit for a login that may.
  const dealerCell = (d) =>
    d.id != null && canDealer ? (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          dealer.openEdit(dealerRowById.get(d.id));
        }}
        aria-label={`Edit ${d.name}`}
        title="Edit dealer"
        className="max-w-full cursor-pointer truncate border-0 bg-transparent p-0 text-left text-[13.5px] text-msd-ink2 hover:text-msd-ink hover:underline"
      >
        {d.name}
      </button>
    ) : (
      <span className="text-[13.5px] text-msd-ink2">{d.name}</span>
    );

  const workshopTr = (w, d) => (
    <tr
      key={w.id}
      onClick={() => setOpenShop(w.id)}
      onKeyDown={(e) =>
        (e.key === "Enter" || e.key === " ") &&
        (e.preventDefault(), setOpenShop(w.id))
      }
      tabIndex={0}
      data-testid="workshop-row"
      aria-label={`Open ${w.location}`}
      className="cursor-pointer border-t border-msd-line hover:bg-msd-ground focus-visible:bg-msd-ground focus-visible:outline-none"
    >
      <td className={cx(CELL, "pl-6")}>
        <span className="text-[14.5px] font-medium text-msd-ink">
          {w.location}
        </span>
      </td>
      <td className={CELL}>{dealerCell(d)}</td>
      <td className={CELL}>
        <span className="inline-flex items-center gap-[6px] text-[13.5px] text-msd-ink2">
          <Dot color={w.status === "paused" ? "#98A2B3" : "#17B26A"} />
          {w.status === "paused" ? "Paused" : "Active"}
        </span>
      </td>
      <td className={cx(CELL, "text-[13.5px] tabular-nums text-msd-ink2")}>
        {w.users ? (
          plural(w.users, "person", "people")
        ) : (
          <span className="text-msd-muted">No team yet</span>
        )}
      </td>
      <td className={CELL}>
        {needsStaff(w) ? (
          <span className="inline-flex items-center gap-[6px] rounded-full bg-[#FFFAEB] px-[10px] py-[3px] text-[12.5px] font-medium text-[#B54708]">
            <Icon name="warn" size={12} stroke={2.4} />
            {plural(w.unfilled, "role", "roles")} missing
          </span>
        ) : (
          <span className="inline-flex items-center gap-[6px] rounded-full bg-[#ECFDF3] px-[10px] py-[3px] text-[12.5px] font-medium text-[#067647]">
            <Icon name="check" size={12} stroke={2.6} />
            Fully staffed
          </span>
        )}
      </td>
      <td className="w-10 pr-4 text-right text-msd-faint">
        <Icon name="right" size={14} stroke={2} />
      </td>
    </tr>
  );

  // A dealer is a column, not a heading: its workshops are rows, and a dealer with none
  // gets one quiet row so it can still be found and edited.
  const dealerTrs = (d) => {
    const shops = see.workshops ? d.workshops.filter(shown) : [];
    if (shops.length) return shops.map((w) => workshopTr(w, d));
    if (view !== "all") return [];
    return [
      <tr key={`d${d.id}`} className="border-t border-msd-line">
        <td className={cx(CELL, "pl-6 text-[13.5px] text-msd-muted")}>
          {see.workshops ? "No workshops yet" : ""}
        </td>
        <td className={CELL} colSpan={cols - 1}>
          {dealerCell(d)}
        </td>
      </tr>,
    ];
  };

  const regionTbody = (r) => {
    const rows = r.dealers.flatMap(dealerTrs);
    if (view !== "all" && !rows.length) return null;
    return (
      <tbody key={r.id} data-testid="region-row" className="group/region">
        <tr className="border-t border-msd-line bg-msd-ground">
          <td colSpan={cols} className="px-6 py-2">
            <div className="flex items-center gap-2">
              <span className="text-[13px] font-semibold text-msd-ink">
                {r.name}
              </span>
              <span className="text-[12.5px] text-msd-muted">
                {plural(r.stats.workshops, "workshop", "workshops")}
              </span>
              {!r.none && canRegion && (
                <LinkBtn
                  className="ml-auto opacity-0 transition-opacity group-hover/region:opacity-100 focus-visible:opacity-100"
                  onClick={() => region.openEdit({ id: r.id, name: r.name })}
                  aria-label={`Edit ${r.name}`}
                >
                  Edit region
                </LinkBtn>
              )}
            </div>
          </td>
        </tr>
        {rows.length ? (
          rows
        ) : (
          <tr className="border-t border-msd-line">
            <td colSpan={cols} className="px-6 py-3 text-[13px] text-msd-muted">
              No dealers here yet.
            </td>
          </tr>
        )}
      </tbody>
    );
  };

  const views = [
    ["all", "All workshops", allShops.length],
    ["gaps", "Needs staff", gapCount],
    ["paused", "Paused", pausedCount],
  ];

  return (
    <Page>
      <PageHead title="Organization" sub={counts.join(" · ")}>
        <AddMenu
          items={[
            canWorkshop &&
              ix.dealers.length > 0 && ["Workshop", () => workshop.openAdd()],
            canDealer && ["Dealer", dealer.openAdd],
            canRegion && ["Region", region.openAdd],
          ].filter(Boolean)}
        />
      </PageHead>

      {nothing ? (
        <Empty title="No regions, dealers or workshops yet">
          {canRegion || canDealer
            ? "Add a region and a dealer, then the dealer's workshops."
            : "Nothing has been set up here yet."}
        </Empty>
      ) : (
        <section className={cx(CARD, "overflow-hidden")}>
          <div className="flex flex-wrap items-end gap-x-6 gap-y-2 px-5 pt-2">
            {see.workshops &&
              views.map(([k, label, n]) => (
                <button
                  key={k}
                  type="button"
                  aria-pressed={view === k}
                  onClick={() => setView(k)}
                  className={cx(
                    "-mb-px flex h-11 cursor-pointer items-center gap-[6px] border-0 border-b-2 bg-transparent px-0 text-[14px]",
                    view === k
                      ? "border-msd-ink font-semibold text-msd-ink"
                      : "border-transparent text-msd-muted hover:text-msd-ink",
                  )}
                >
                  {label}
                  <span
                    className={cx(
                      "tabular-nums",
                      "rounded-full bg-msd-ground px-[7px] py-[1px] text-[12px] text-msd-muted",
                    )}
                  >
                    {n}
                  </span>
                </button>
              ))}
            <label className="mb-[6px] ml-auto flex h-8 self-center w-[240px] max-w-full items-center gap-2 rounded-[10px] bg-msd-ground px-3 text-msd-muted max-[640px]:ml-0 max-[640px]:w-full">
              <Icon name="search" size={14} stroke={2} />
              <input
                type="search"
                value={filters.query}
                onChange={(e) => set({ query: e.target.value })}
                placeholder="Search"
                aria-label="Search regions, dealers and workshops"
                data-testid="org-search"
                className="min-w-0 grow border-0 bg-transparent text-[13.5px] text-msd-ink outline-none placeholder:text-msd-faint"
              />
            </label>
          </div>
          {tree.length === 0 ? (
            <p className="m-0 border-t border-msd-line px-4 py-10 text-center text-[14px] text-msd-muted">
              Nothing matches that search.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] border-collapse">
                {see.workshops && (
                  <thead>
                    <tr className="border-t border-msd-line">
                      <th className={cx(HEAD, "pl-6")}>Workshop</th>
                      <th className={HEAD}>Dealer</th>
                      <th className={HEAD}>Status</th>
                      <th className={HEAD}>Team</th>
                      <th className={HEAD}>Staffing</th>
                      <th className="w-10" aria-hidden="true" />
                    </tr>
                  </thead>
                )}
                {tree.map(regionTbody)}
              </table>
            </div>
          )}
        </section>
      )}

      {/* Region */}
      <Sheet
        open={region.open && !region.deleting}
        title={region.editing ? "Edit region" : "Add region"}
        onClose={() => region.setOpen(false)}
        onSubmit={region.submit}
        testid="region-sheet"
        footer={
          <>
            {region.editing && (
              <RemoveBtn onClick={() => region.askDelete(region.editing.id)}>
                Delete
              </RemoveBtn>
            )}
            <Btn onClick={() => region.setOpen(false)}>Cancel</Btn>
            <Btn type="submit" variant="pri" disabled={region.pending}>
              {region.editing ? "Save" : "Add region"}
            </Btn>
          </>
        }
      >
        <Field
          label="Region name"
          error={region.errors.name}
          hint="Dealers are placed in a region from each dealer's Edit."
        >
          <input
            type="text"
            className={SMALL_INPUT}
            value={region.name}
            placeholder="West"
            autoFocus
            onChange={(e) => region.setName(e.target.value)}
          />
        </Field>
      </Sheet>
      <ConfirmDelete
        open={!!region.deleting}
        onClose={() => region.setDeleting(null)}
        name={region.deleting?.name || ""}
        body="This cannot be undone."
        blocked={region.deleting?.blocked}
        onConfirm={deleteAndClose(region, () => region.setOpen(false))}
        pending={region.pending}
      />

      {/* Dealer */}
      <Sheet
        open={dealer.open && !dealer.deleting}
        title={dealer.editing ? "Edit dealer" : "Add dealer"}
        onClose={() => dealer.setOpen(false)}
        onSubmit={dealer.submit}
        testid="dealer-sheet"
        footer={
          <>
            {dealer.editing && (
              <RemoveBtn onClick={() => dealer.askDelete(dealer.editing.id)}>
                Delete
              </RemoveBtn>
            )}
            <Btn onClick={() => dealer.setOpen(false)}>Cancel</Btn>
            <Btn type="submit" variant="pri" disabled={dealer.pending}>
              {dealer.editing ? "Save" : "Add dealer"}
            </Btn>
          </>
        }
      >
        <Field label="Dealer name" error={dealer.errors.name}>
          <input
            type="text"
            className={SMALL_INPUT}
            value={dealer.form.name}
            placeholder="Kataria Motors"
            autoFocus
            onChange={(e) =>
              dealer.setForm({ ...dealer.form, name: e.target.value })
            }
          />
        </Field>
        <Field label="Dealer code" error={dealer.errors.dealer_code}>
          <input
            type="text"
            className={SMALL_INPUT}
            value={dealer.form.dealer_code}
            placeholder="KM-01"
            onChange={(e) =>
              dealer.setForm({ ...dealer.form, dealer_code: e.target.value })
            }
          />
        </Field>
        <FieldGroup
          label="Regions"
          hint={
            ix.regions.length
              ? "A dealer can serve more than one region."
              : "No regions yet. A dealer can be added without one and placed later."
          }
        >
          {ix.regions.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {ix.regions.map((r) => {
                const on = dealer.form.region_ids.includes(r.id);
                return (
                  <button
                    key={r.id}
                    type="button"
                    role="switch"
                    aria-checked={on}
                    onClick={() => dealer.toggleRegion(r.id)}
                    className={cx(
                      "flex h-9 cursor-pointer items-center gap-[6px] rounded-full border-0 px-[14px] text-[14px]",
                      on
                        ? "bg-msd-btn font-medium text-white"
                        : "bg-msd-ground text-msd-ink2 hover:bg-msd-soft",
                    )}
                  >
                    {on && <Icon name="check" size={14} stroke={2.6} />}
                    {r.name}
                  </button>
                );
              })}
            </div>
          )}
        </FieldGroup>
      </Sheet>
      <ConfirmDelete
        open={!!dealer.deleting}
        onClose={() => dealer.setDeleting(null)}
        name={dealer.deleting?.name || ""}
        body="This cannot be undone."
        blocked={dealer.deleting?.blocked}
        onConfirm={deleteAndClose(dealer, () => dealer.setOpen(false))}
        pending={dealer.pending}
      />

      {/* Adding a workshop */}
      <Sheet
        open={workshop.adding}
        title="Add workshop"
        onClose={() => workshop.setAdding(false)}
        onSubmit={workshop.submitAdd}
        testid="workshop-add-sheet"
        footer={
          <>
            <Btn onClick={() => workshop.setAdding(false)}>Cancel</Btn>
            <Btn type="submit" variant="pri" disabled={workshop.pending}>
              Add workshop
            </Btn>
          </>
        }
      >
        <Field label="Workshop name" error={workshop.errors.workshop_location}>
          <input
            type="text"
            className={SMALL_INPUT}
            value={workshop.form.workshop_location}
            placeholder="Bandra"
            autoFocus
            onChange={(e) =>
              workshop.setForm({
                ...workshop.form,
                workshop_location: e.target.value,
              })
            }
          />
        </Field>
        {/* A plain group, not a <label>: a label would hand every click in the menu back to its button. */}
        <FieldGroup label="Dealer" error={workshop.errors.dealer_id}>
          <Picker
            field
            label="Dealer"
            options={ix.dealers.map((d) => {
              const regions = regionNamesForDealer(d, ix);
              return [
                String(d.id),
                regions.length ? `${d.name} · ${regions.join(", ")}` : d.name,
              ];
            })}
            value={String(workshop.form.dealer_id)}
            onChange={(v) =>
              workshop.setForm({ ...workshop.form, dealer_id: v })
            }
          />
        </FieldGroup>
      </Sheet>

      {/* One workshop: its team, its status, deleting it */}
      <WorkshopSheet
        ix={ix}
        workshopId={openShop}
        onClose={() => setOpenShop(null)}
        editor={workshop}
        canEdit={canWorkshop}
        canEditPeople={access.people.edit}
        tenantId={tenantId}
      />
    </Page>
  );
}
